/**
 * Whether this browser can actually run our map.
 *
 * Two things the previous inline probe got wrong, both of which showed a working
 * device the "map couldn't load" notice:
 *
 *  1. It created a WebGL context on **every mount and never released it**.
 *     Browsers cap the number of live WebGL contexts (Chrome around 16, Safari
 *     fewer) and drop the oldest or refuse new ones once the cap is hit. Because
 *     the search map remounts on view changes and the listing map probes too,
 *     enough navigation exhausted the pool — after which the probe reported
 *     "unsupported" on hardware that runs the map perfectly well.
 *  2. It accepted WebGL **1** as sufficient. Mapbox GL v3 requires WebGL 2, so a
 *     WebGL1-only device passed the probe and then failed inside Mapbox instead,
 *     which is a worse failure than the honest notice.
 *
 * Probed at most once per page load, cached, and the context is handed straight
 * back via WEBGL_lose_context so the probe costs nothing for the page's life.
 */
let cached: boolean | undefined;

export function supportsMapGL(): boolean {
  if (typeof window === 'undefined') return true;
  if (cached !== undefined) return cached;

  try {
    const canvas = document.createElement('canvas');
    // Mapbox GL v3 needs WebGL 2 specifically.
    const gl = canvas.getContext('webgl2') as WebGL2RenderingContext | null;
    if (!gl) {
      cached = false;
      return cached;
    }
    // Give the context back immediately. A capability probe must never hold one
    // of the handful the browser allows.
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    cached = true;
  } catch {
    // A thrown getContext (blocked by policy, GPU process gone) means no map.
    cached = false;
  }
  return cached;
}

/** Test seam: forget the cached result so a probe can be re-run. */
export function resetMapSupportCache(): void {
  cached = undefined;
}
